/* ──────────────────────────────────────────────────────────────────────────
   DetailShell — modal-edit ekranlarını tam-sayfa /entity/:id detaya çevirmek
   için ortak iskelet. ProductDetailPage'in cilalı kart/grid tasarımını
   yeniden kullanılabilir parçalara böler.

   Kullanım:
     <DetailShell crumb={[{label:'Personeller',to:'/personnel'},{label:'Detay',current:true}]}
                  status={<StatusBadge .../>}
                  tabs={[{key,label,icon,disabled}]} activeTab onTab
                  actions={<button className="dps-btn primary">…</button>}
                  loading>
       <Section icon="user" title="…" subtitle="…">
         <Grid cols={2}>
           <FieldText form={form} set={set} k="firstName" label="Ad" req tried={tried} />
           …
         </Grid>
       </Section>
     </DetailShell>
   ────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { loadAuditUserMap, resolveAuditUser } from './auditUsers';
import { ChangeHistoryDrawer } from './ChangeHistoryDrawer';
import { usePageActions } from '../../contexts/PermissionContext';
import './detailShell.css';

// #270 Salt-okunur (görüntüleme) modu — DetailShell readOnly ile açılır; alt alan
// bileşenleri (FieldText/FieldSelect/FieldCustom…) bunu okuyup düzenlemeyi kapatır.
// İleride yetkisel "düzenleme kısıtı" için de tek kaynak.
const ReadOnlyContext = createContext(false);
export const useDetailReadOnly = () => useContext(ReadOnlyContext);

// #282 "İzle" (görüntüleme) modu. Kural tersine çevrildi: mevcut bir kaydın detayı
// HER ZAMAN salt-okunur açılır (çift tık, göz simgesi, doğrudan URL — hepsi izleme).
// Düzenlemek açık bir niyet ister: listedeki kalem simgesi (?edit=1), navigate state'i
// { edit:true } ya da "/edit" ile biten bir rota. Böylece hiçbir liste/detay ekranına
// tek tek dokunmadan tüm uygulamada aynı davranış geçerli olur.
export function useIsEditIntent() {
  const loc = useLocation();
  const [sp] = useSearchParams();
  return loc?.state?.edit === true || sp.get('edit') === '1' || (loc?.pathname || '').endsWith('/edit');
}

// Geriye dönük uyumluluk: bazı ekranlar (Temsilci, Kullanıcı, Rol, Tanım) kendi
// "izle" dallarını bu hook ile kuruyor. Artık düzenleme niyetinin yokluğu = izleme.
export function useIsViewMode() {
  const loc = useLocation();
  const editIntent = useIsEditIntent();
  const isNewRoute = (loc?.pathname || '').endsWith('/new');
  return !isNewRoute && !editIntent;
}

// ── ikon seti (inline SVG path parçaları) ──
const ICONS = {
  chevR: <polyline points="9 18 15 12 9 6" />,
  chevD: <polyline points="6 9 12 15 18 9" />,
  chevL: <polyline points="15 18 9 12 15 6" />,
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  x: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
  check: <polyline points="20 6 9 17 4 12" />,
  copy: <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
  info: <><circle cx="12" cy="12" r="9" /><line x1="12" y1="11" x2="12" y2="16" /><line x1="12" y1="8" x2="12" y2="8" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  users: <><path d="M17 21v-2a4 4 0 0 0-3-3.87" /><path d="M9 21v-2a4 4 0 0 0-4-4H4" /><circle cx="9" cy="7" r="4" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22 6 12 13 2 6" /></>,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />,
  briefcase: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>,
  box: <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><polyline points="3.3 7 12 12 20.7 7" /><line x1="12" y1="22" x2="12" y2="12" /></>,
  layers: <><polygon points="12 2 22 8.5 12 15 2 8.5 12 2" /><polyline points="2 15.5 12 22 22 15.5" /></>,
  list: <><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3.5" y1="6" x2="3.6" y2="6" /><line x1="3.5" y1="12" x2="3.6" y2="12" /><line x1="3.5" y1="18" x2="3.6" y2="18" /></>,
  mapPin: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z" /><circle cx="12" cy="10" r="3" /></>,
  wallet: <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></>,
  hash: <><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></>,
  truck: <><rect x="1" y="5" width="13" height="11" rx="1.5" /><path d="M14 9h4l3 3v4h-7" /><circle cx="6" cy="18.5" r="1.8" /><circle cx="17.5" cy="18.5" r="1.8" /></>,
  tag: <><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 2.8 12V4.8A2 2 0 0 1 4.8 2.8H12a2 2 0 0 1 1.4.6l7.2 7.2a2 2 0 0 1 0 2.8Z" /><circle cx="7.5" cy="7.5" r="1.3" /></>,
  grid: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 16 14" /></>,
  history: <><path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" /><polyline points="12 7 12 12 15 14" /></>,
  arrowRight: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
  pencil: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  eye: <><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" /><circle cx="12" cy="12" r="3" /></>,
};

export function Icon({ name, size = 18 }) {
  const p = ICONS[name];
  if (!p) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{p}</svg>
  );
}

// Kayıt denetim (audit) bilgisi — üzerine gelince oluşturulma/güncelleme tarih + kişi gösterir.
// Tüm detay ekranlarında tekrar kullanılmak üzere DetailShell'e `audit` prop'u ile verilir.
function fmtAuditDateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function AuditInfo({ audit }) {
  const [userMap, setUserMap] = useState(null);
  useEffect(() => {
    let alive = true;
    loadAuditUserMap().then((m) => { if (alive) setUserMap(m); });
    return () => { alive = false; };
  }, []);

  const createdBy = audit && (audit.createdBy ?? audit.CreatedBy);
  const createdDate = audit && (audit.createdDate ?? audit.CreatedDate);
  const updatedBy = audit && (audit.updatedBy ?? audit.UpdatedBy);
  const updatedDate = audit && (audit.updatedDate ?? audit.UpdatedDate);
  // Hiç audit verisi yoksa butonu gösterme (ör. backend GetById bu alanları döndürmüyorsa).
  if (!audit || (!createdBy && !createdDate && !updatedBy && !updatedDate)) return null;

  return (
    <div className="dps-audit">
      <button type="button" className="dps-audit-btn" aria-label="Kayıt bilgisi" title="Kayıt bilgisi">
        <Icon name="info" size={17} />
      </button>
      <div className="dps-audit-pop" role="tooltip">
        <div className="dps-audit-title">Kayıt Bilgisi</div>
        <div className="dps-audit-row"><span className="dps-audit-k">Oluşturulma</span><span className="dps-audit-v">{fmtAuditDateTime(createdDate)}</span></div>
        <div className="dps-audit-row"><span className="dps-audit-k">Oluşturan</span><span className="dps-audit-v">{resolveAuditUser(createdBy, userMap)}</span></div>
        <div className="dps-audit-row"><span className="dps-audit-k">Son Güncelleme</span><span className="dps-audit-v">{fmtAuditDateTime(updatedDate)}</span></div>
        <div className="dps-audit-row"><span className="dps-audit-k">Güncelleyen</span><span className="dps-audit-v">{resolveAuditUser(updatedBy, userMap)}</span></div>
      </div>
    </div>
  );
}

// Değişiklik geçmişi (⟳) butonu — audit info butonunun yanında; tıklanınca sağdan çekmece açar.
// `history` prop şekli: { entityName, entityId, title? }. Verilmezse buton hiç render edilmez.
export function HistoryButton({ onClick }) {
  return (
    <button type="button" className="dps-hist-btn" onClick={onClick} aria-label="Değişiklik geçmişi" title="Değişiklik geçmişi">
      <Icon name="history" size={17} />
    </button>
  );
}

export function DetailShell({ crumb = [], status, tabs, activeTab, onTab, actions, loading, readOnly = false, modeless = false, viewActions, audit, history, aside, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [histOpen, setHistOpen] = useState(false);
  // #287/#900: Menü aksiyon yetkisi — Düzenle(Update) hakkı yoksa form salt-okunur açılır
  // (yeni kayıt sayfasında Yeni(Add) hakkına bakılır). Yetki yoksa/yüklenmediyse default-open.
  const { canEdit, canAdd } = usePageActions();
  const isNewRoute = (location?.pathname || '').endsWith('/new');
  const permLocked = isNewRoute ? !canAdd : !canEdit;

  // #282: mevcut kayıt (rota parametresi olan, /new olmayan) düzenleme niyeti gelmedikçe
  // izleme modundadır. `modeless` kayıt formu olmayan shell'ler (rapor detayı, pano…) içindir.
  const hasRecordId = Object.values(params || {}).some((v) => v && v !== 'new');
  const editIntent = useIsEditIntent();
  const [editing, setEditing] = useState(editIntent);
  useEffect(() => { setEditing(editIntent); }, [editIntent, location.pathname]);
  // Düzenleme niyeti (?edit=1) Update hakkını YENEMEZ: hak yoksa izleme moduna düşülür. Aksi halde
  // gate'i unutmuş bir listedeki kalem ya da elle yazılan URL, salt-okunur formu sayfanın kendi
  // Kaydet/Sil çubuğuyla açardı.
  const viewMode = !modeless && !isNewRoute && hasRecordId && (!editing || permLocked);

  const effReadOnly = readOnly || permLocked || viewMode;

  // ── #851 Eksik zorunlu alan bildirimi — GENEL KURAL ────────────────────────
  // Talep: "Zorunlu bir alanı doldurmadan kaydet dersek 'zorunlu alanı doldurun'
  // diyor ama hangi alan olduğunu ve o kısma götürmüyor."
  //
  // Bu, tek bir ekranın değil TÜM detay ekranlarının sorunuydu: her sayfa kendi
  // zorunluluk listesini tutup kendi genel mesajını basıyordu. Kural bu yüzden
  // sayfalara değil KABUĞA konur — Field* bileşenleri geçersiz alanı zaten
  // `.dps-ff.is-invalid` olarak çiziyor; kabuk o alanları okuyup adlarıyla listeler
  // ve ilkine götürür. Yeni bir ekran hiçbir şey yazmadan bu davranışı devralır.
  //
  // SINIR: yalnız DOM'daki alanlar görülür. Başka bir sekmede duran (henüz render
  // edilmemiş) zorunlu alan listeye giremez; sayfa o durumda ilgili sekmeye kendisi
  // geçmelidir (ör. ProductDetailPage'in Ek Bilgiler sekmesi).
  const mainRef = useRef(null);
  const [missingFields, setMissingFields] = useState([]);
  const missingSigRef = useRef('');

  useEffect(() => {
    const root = mainRef.current;
    if (!root) return;
    const found = Array.from(root.querySelectorAll('.dps-ff.is-invalid')).map((el) => ({
      key: el.getAttribute('data-field') || '',
      // Etiketteki zorunluluk yıldızı ayrı bir <i>* düğümüdür; metne karışmasın.
      label: (el.querySelector('.dps-ff-lbl')?.textContent || '').replace(/\*\s*$/, '').trim(),
      // Alana özgü mesaj (ör. "IBAN geçersiz") varsa onu göster; yoksa alan boştur.
      message: (el.querySelector('.dps-ff-msg')?.textContent || '').trim(),
    })).filter((f) => f.label);

    const sig = found.map((f) => `${f.key}|${f.label}|${f.message}`).join(',');
    if (sig === missingSigRef.current) return; // değişmediyse döngü kurma
    const wasClean = missingSigRef.current === '';
    missingSigRef.current = sig;
    setMissingFields(found);
    // Kullanıcıyı YALNIZ temizden hatalıya geçişte (yani Kaydet'e bastığında) taşı;
    // hatalarını düzeltirken her tuşta sayfayı zıplatmak rahatsız edici olurdu.
    if (found.length && wasClean) focusField(found[0].key, { container: root });
  });

  // İzleme modunda sayfanın kendi aksiyonları (Kaydet/Sil) hiç render edilmez; yerine
  // "Kapat" + "Düzenle" gelir. Düzenle URL'ye ?edit=1 yazar → yenilemede mod korunur.
  const listTo = crumb.find((c) => c.to)?.to;
  const startEditing = () => {
    setEditing(true);
    const sp = new URLSearchParams(location.search);
    sp.set('edit', '1');
    navigate(`${location.pathname}?${sp.toString()}`, { replace: true, state: location.state });
  };
  // Yeni kayıt sayfasına Add hakkı olmadan (elle URL) gelindiyse sayfanın Kaydet'i hiç çizilmez —
  // form zaten salt-okunur, aksiyon çubuğu da onunla tutarlı olmalı.
  const newRouteLocked = !modeless && isNewRoute && permLocked;
  const shellActions = viewMode ? (
    <>
      {viewActions}
      {listTo && <button type="button" className="dps-btn ghost" onClick={() => navigate(listTo)}>Kapat</button>}
      {canEdit && (
        <button type="button" className="dps-btn primary" onClick={startEditing}>
          <Icon name="pencil" size={16} /> Düzenle
        </button>
      )}
    </>
  ) : newRouteLocked ? (
    listTo && <button type="button" className="dps-btn ghost" onClick={() => navigate(listTo)}>Kapat</button>
  ) : actions;

  return (
    <ReadOnlyContext.Provider value={effReadOnly}>
    <div className={'dps' + (effReadOnly ? ' is-ro' : '')}>
      <div className="dps-main" ref={mainRef}>
        <header className="dps-head">
          <div className="dps-crumb">
            {crumb.map((c, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {i > 0 && <Icon name="chevR" size={14} />}
                {c.current
                  ? <b>{c.label}</b>
                  : <span style={{ cursor: c.to ? 'pointer' : 'default' }} onClick={() => c.to && navigate(c.to)}>{c.label}</span>}
              </span>
            ))}
            {status && <span className="dps-crumb-status">{status}</span>}
            {viewMode && <span className="dps-ro-chip"><Icon name="eye" size={14} /> İzleme</span>}
          </div>

          {/* Kayıt bilgisi (audit) + Değişiklik geçmişi — detay tab'ının solunda. */}
          {(audit || history) && (
            <div className="dps-meta-btns">
              {audit && <AuditInfo audit={audit} />}
              {history && history.entityName && history.entityId && (
                <HistoryButton onClick={() => setHistOpen(true)} />
              )}
            </div>
          )}
          {history && history.entityName && history.entityId && (
            <ChangeHistoryDrawer
              open={histOpen}
              onClose={() => setHistOpen(false)}
              entityName={history.entityName}
              entityId={history.entityId}
              title={history.title}
              audit={audit}
            />
          )}

          {tabs && tabs.length > 0 && (
            <div className="dps-tabs">
              {tabs.map((t) => (
                <button key={t.key} className={activeTab === t.key ? 'is-on' : ''}
                        disabled={t.disabled} title={t.title}
                        onClick={() => !t.disabled && onTab(t.key)}>
                  {t.icon && <Icon name={t.icon} size={15} />}{t.label}
                  {t.count != null && <span className="dps-tab-cnt">{t.count}</span>}
                </button>
              ))}
            </div>
          )}

          {shellActions && <div className="dps-actions">{shellActions}</div>}
        </header>

        {/* #851 Eksik/geçersiz alanların adlarıyla listesi. Kaydet'ten sonra görünür,
            alanlar düzeltildikçe kendiliğinden küçülür ve biter. */}
        {missingFields.length > 0 && (
          <div className="dps-reqbar" role="alert" aria-live="polite">
            <Icon name="info" size={16} />
            <span>
              <b>Eksik/hatalı alan{missingFields.length > 1 ? 'lar' : ''}:</b>{' '}
              {missingFields.map((f, i) => (
                <span key={`${f.key}-${i}`}>
                  {i > 0 && ', '}
                  <button type="button" className="dps-reqbar-link"
                          onClick={() => focusField(f.key, { container: mainRef.current })}>
                    {f.label}
                  </button>
                  {f.message && f.message !== 'Zorunlu alan' && ` (${f.message})`}
                </span>
              ))}
            </span>
          </div>
        )}

        {loading ? <div className="dps-loading">Yükleniyor…</div> : (
          aside ? (
            <div className="dps-layout">
              <aside className="dps-aside sfa">{aside}</aside>
              <div className="dps-content">{children}</div>
            </div>
          ) : children
        )}
      </div>
    </div>
    </ReadOnlyContext.Provider>
  );
}

export function Section({ icon, title, subtitle, right, note, children }) {
  return (
    <section className="dps-blk">
      <div className="dps-sec-head">
        {icon && <span className="dps-sec-ic"><Icon name={icon} size={19} /></span>}
        <div className="dps-sec-tt"><h3>{title}</h3>{subtitle && <p>{subtitle}</p>}</div>
        {right}
      </div>
      {note && <div className="dps-note"><Icon name="info" size={16} /> {note}</div>}
      {children}
    </section>
  );
}

export function Grid({ cols = 2, children }) {
  return <div className={cols === 3 ? 'dps-grid3' : 'dps-grid2'}>{children}</div>;
}

// ── alan bileşenleri (form/set(k,v)/tried modeli) ──
function wrapClass(invalid, span) {
  return 'dps-ff' + (invalid ? ' is-invalid' : '') + (span ? ' dps-span2' : '');
}

/**
 * #851 — "Zorunlu bir alanı doldurmadan kaydet dersek 'zorunlu alanı doldurun' diyor
 * ama hangi alan olduğunu söylemiyor ve o kısma götürmüyor."
 *
 * Eksik alan zaten kırmızı çiziliyordu; sorun uzun formda o alanın ekranın DIŞINDA
 * kalmasıydı — kullanıcı genel bir hata mesajıyla baş başa kalıyordu. Alan
 * sarmalayıcıları artık `data-field` taşır; bu yardımcı ilk eksik alanı bulup görünür
 * alana kaydırır ve odaklar.
 *
 * Çağrı, sekme değiştiren bir setState'ten HEMEN SONRA yapılabilsin diye iş bir sonraki
 * makroya bırakılır: hedef alan yeni sekmeyle birlikte henüz DOM'a girmemiş olabilir.
 *
 * @param {string} key Alanın form anahtarı (FieldText/FieldSelect'teki `k`).
 * @param {object} [opts]
 * @param {ParentNode} [opts.container] Arama kökü; verilmezse tüm belge.
 */
export function focusField(key, { container } = {}) {
  if (!key) return;
  setTimeout(() => {
    try {
      const root = container || document;
      const safe = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(String(key)) : String(key);
      const el = root.querySelector(`[data-field="${safe}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = el.querySelector('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])');
      if (focusable) focusable.focus({ preventScroll: true });
    } catch { /* odaklama bir kolaylıktır; başarısız olursa kayıt akışını bozmasın */ }
  }, 0);
}

// mask: KVKK onayı yokken hassas alanı ••••• gösterir (veri form state'inde durur,
//       backend'e gerçek değer gider). format: onChange'de değeri biçimler (telefon vb.).
// `error`: alana ÖZGÜ doğrulama mesajı (ör. IBAN kontrol basamağı). Verildiğinde alan geçersiz
// çizilir ve mesaj "Zorunlu alan"ın yerine geçer — kullanıcıya neyin yanlış olduğunu söylemeyen
// genel bir uyarı, hatayı bulmasına yardım etmiyor. Boş/yoksa davranış aynen eskisi gibi.
export function FieldText({ form, set, tried, k, label, req, mono, num, type = 'text', suffix, placeholder, span, readOnly, mask, format, hint, error }) {
  const raw = form[k] ?? '';
  const invalid = (tried && req && !String(raw).trim()) || !!error;
  const locked = readOnly || mask;
  const shown = mask ? (String(raw).length ? '••••••' : '') : raw;
  // Sayısal alanlar (num) native type="number" yerine inputMode="decimal" metin girişi
  // kullanır: native number input bazı yerel ayarlarda (ör. tr-TR ondalık ayracı) elle
  // yazmayı engelleyip yalnız artır/azalt oklarına izin veriyordu. Metin + filtre ile
  // klavyeden serbest yazılır; virgül noktaya çevrilir, geçersiz karakterler atılır.
  const roCtx = useDetailReadOnly();
  const effLocked = locked || roCtx;
  const numeric = num && !mask;
  const inputType = mask ? 'text' : (numeric ? 'text' : type);
  const handleChange = (e) => {
    let v = e.target.value;
    // #771.1 Biçimlendiriciye ÖNCEKİ değer de verilir: maskeli alanlarda (telefon) Backspace
    // bir ayıracı sildiğinde rakamlar değişmez ve ayıraç anında geri yazılırdı → tuş hiç
    // çalışmıyor gibi görünüyordu. Ayıraç silmesini ayırt etmek için `raw` gerekiyor.
    // İkinci argümanı yok sayan mevcut biçimlendiriciler etkilenmez.
    if (format) v = format(v, raw);
    else if (numeric) v = v.replace(',', '.').replace(/[^0-9.\-]/g, '');
    set(k, v);
  };
  return (
    <div className={wrapClass(invalid, span)} data-field={k}>
      <div className="dps-ff-lbl">{label}{req && <i>*</i>}</div>
      <div className="dps-ff-input">
        <input type={inputType} className={mono ? 'mono' : (num ? 'num' : '')} inputMode={num ? 'decimal' : undefined}
               readOnly={effLocked} disabled={effLocked}
               value={shown} placeholder={placeholder || ''}
               onChange={handleChange} />
        {suffix && <span className="dps-ff-suffix">{suffix}</span>}
      </div>
      {invalid ? <div className="dps-ff-msg">{error || 'Zorunlu alan'}</div> : (hint && <div className="dps-ff-hint">{hint}</div>)}
    </div>
  );
}

export function FieldSelect({ form, set, tried, k, label, options = [], req, span, hint, onChange, disabled }) {
  const roCtx = useDetailReadOnly();
  const invalid = tried && req && (form[k] === '' || form[k] === null || form[k] === undefined);
  return (
    <div className={wrapClass(invalid, span)} data-field={k}>
      <div className="dps-ff-lbl">{label}{req && <i>*</i>}</div>
      <div className="dps-ff-input">
        <select value={form[k] ?? ''} disabled={roCtx || disabled} onChange={(e) => (onChange ? onChange(e.target.value) : set(k, e.target.value))}>
          {options.map((o) => (typeof o === 'object'
            ? <option key={o.value} value={o.value}>{o.label}</option>
            : <option key={o} value={o}>{o}</option>))}
        </select>
        <span className="dps-ff-chev"><Icon name="chevD" size={16} /></span>
      </div>
      {invalid ? <div className="dps-ff-msg">Zorunlu alan</div> : (hint && <div className="dps-ff-hint">{hint}</div>)}
    </div>
  );
}

export function FieldDate({ form, set, tried, k, label, req, span, readOnly }) {
  const roCtx = useDetailReadOnly();
  const effLocked = readOnly || roCtx;
  const invalid = tried && req && !form[k];
  return (
    <div className={wrapClass(invalid, span)} data-field={k}>
      <div className="dps-ff-lbl">{label}{req && <i>*</i>}</div>
      <div className="dps-ff-input">
        <input type="date" value={form[k] ?? ''} readOnly={effLocked} disabled={effLocked}
               onChange={(e) => set(k, e.target.value)} />
      </div>
      {invalid && <div className="dps-ff-msg">Zorunlu alan</div>}
    </div>
  );
}

export function FieldTextarea({ form, set, k, label, rows = 3, placeholder, span, readOnly }) {
  const roCtx = useDetailReadOnly();
  const effLocked = readOnly || roCtx;
  return (
    <div className={wrapClass(false, span)} data-field={k}>
      <div className="dps-ff-lbl">{label}</div>
      <div className="dps-ff-input area">
        <textarea rows={rows} value={form[k] ?? ''} placeholder={placeholder || ''}
                  readOnly={effLocked} disabled={effLocked}
                  onChange={(e) => set(k, e.target.value)} />
      </div>
    </div>
  );
}

// Özel kontrol (RelationPicker / PersonnelPickerDropdown vb.) sarıcısı.
// Açık/kapalı anahtarı. Bir onay kutusu da işi görürdü; ama e-belge ekranındaki anahtarlar
// ("E-Fatura kesebilir", "E-Arşiv offline kaydedilebilsin") bir DAVRANIŞI açıp kapatıyor —
// onay kutusu "kabul ediyorum" çağrışımı taşır, anahtar "açık/kapalı" durumu.
// hint: anahtarın altında küçük açıklama; disabled: salt-okunur bağlamda da kilitlenir.
export function FieldToggle({ form, set, k, label, hint, span, disabled, onChange }) {
  const roCtx = useDetailReadOnly();
  const locked = disabled || roCtx;
  const on = !!form[k];
  const toggle = () => {
    if (locked) return;
    const next = !on;
    if (onChange) onChange(next);
    else set(k, next);
  };
  return (
    <div className={'dps-ff dps-ff-tg' + (span ? ' dps-span2' : '')}>
      <button type="button" role="switch" aria-checked={on} aria-label={label}
              className={'dps-switch' + (on ? ' is-on' : '') + (locked ? ' is-locked' : '')}
              onClick={toggle} disabled={locked}>
        <span className="dps-switch-knob" />
      </button>
      <div className="dps-ff-tg-tt">
        <span>{label}</span>
        {hint && <small>{hint}</small>}
      </div>
    </div>
  );
}

// fieldKey: #851 odaklama için form anahtarı. Opsiyoneldir — mevcut çağrıların
// hiçbirini değiştirmez, yalnız "eksik alana götür" isteyen ekranlar verir.
export function FieldCustom({ label, req, invalid = false, span, children, fieldKey }) {
  const roCtx = useDetailReadOnly();
  return (
    <div className={wrapClass(invalid, span)} data-field={fieldKey}>
      <div className="dps-ff-lbl">{label}{req && <i>*</i>}</div>
      {roCtx ? <div className="dps-ff-ro" aria-disabled="true">{children}</div> : children}
    </div>
  );
}

// Split (dropdown) buton: ana aksiyon + caret ile ek aksiyonlar (örn. Kaydet ve Yeni).
export function SplitButton({ label, onClick, disabled, icon = 'check', menu = [] }) {
  const [open, setOpen] = useState(false);
  const hasMenu = menu && menu.length > 0;
  return (
    <div className={'dps-split' + (open ? ' is-open' : '')}>
      <button className={'dps-btn primary' + (hasMenu ? ' dps-split-main' : '')} onClick={onClick} disabled={disabled}>
        {icon && <Icon name={icon} size={17} />} {label}
      </button>
      {hasMenu && (
        <button className="dps-btn primary dps-split-caret" onClick={() => setOpen((o) => !o)}
                disabled={disabled} aria-haspopup="true" aria-expanded={open} aria-label="Diğer kaydetme seçenekleri">
          <Icon name="chevD" size={15} />
        </button>
      )}
      {open && hasMenu && (
        <>
          <div className="dps-split-bd" onClick={() => setOpen(false)} />
          <div className="dps-split-menu" role="menu">
            {menu.map((m, i) => (
              <button key={i} role="menuitem" onClick={() => { setOpen(false); m.onClick(); }} disabled={m.disabled}>
                {m.icon && <span className="dps-split-ic"><Icon name={m.icon} size={16} /></span>}
                <span className="dps-split-tt"><b>{m.label}</b>{m.desc && <span>{m.desc}</span>}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Alt-kayıt hızlı düzenleme modalı (ana kayıt tam-sayfa kalır; alt-grid kaydı için).
export function SubModal({ title, onClose, footer, maxWidth, children }) {
  return (
    <div className="dps-overlay" onClick={onClose}>
      <div className="dps-modal" style={maxWidth ? { maxWidth } : undefined} onClick={(e) => e.stopPropagation()}>
        <div className="dps-modal-head">
          <h3>{title}</h3>
          <button className="dps-modal-x" onClick={onClose}>×</button>
        </div>
        <div className="dps-modal-body">{children}</div>
        {footer && <div className="dps-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export default DetailShell;
